import { FormControl, FormErrorMessage, FormHelperText, Link, Select, Spinner } from '@chakra-ui/react'
import type { BaseAddressDto } from 'BrokerTsLib/src/dtos'
import type { FieldError } from 'BrokerTsLib/src/rhf'
import { useFormContext, useWatch } from 'BrokerTsLib/src/rhf'
import { parse } from 'BrokerTsLib/src/Utils/formatInputs'
import { useEffect, useState } from 'react'
import { mask } from 'remask'

import { FormLabel } from '../../../components/FormLabel'
import { InputGroup } from '../../../components/InputGroup'
import { useRegions } from '../../../hooks'
import { useNameof } from '../../../hooks/useNameof'
import { FormRow } from '../FormRow'

export const formatPsc = (value?: string) => {
  if (!value) return undefined

  return mask(value.toString(), ['SSS SS'])
}

function InsureeAddress({ addressPrefix, street = false, isDisabled = false, isDisabledStreet }:
{
  addressPrefix: string,
  street?: boolean,
  isDisabled?: boolean,
  isDisabledStreet?: boolean
}) {

  const banameof = useNameof<BaseAddressDto>(addressPrefix)
  const pcPath = banameof(x => x.postCode)
  const cityPath = banameof(x => x.city)
  const streetPath = banameof(x => x.street)
  const streetNumberPath = banameof(x => x.streetNumber)

  const {
    register,
    getFieldState,
    setValue,
    trigger
  } = useFormContext()

  const postCodeField = useWatch({ name: pcPath }) ?? ''
  const [validPsc, setValidPsc] = useState(false)

  const pcError = getFieldState(pcPath).error
  const cityError = getFieldState(cityPath).error
  const streetError = getFieldState(streetPath).error
  const streetNumberError = getFieldState(streetNumberPath).error

  const { data: citiesData, isLoading: isLoadingCities } = useRegions(postCodeField, { enabled: validPsc })
  const cities = validPsc ? citiesData?.data.filter(x => (x.city?.length ?? 0) > 0) ?? [] : []

  // predvybrani obce, pokud je v nabidce jen jedna
  useEffect(() => {
    if (cities.length == 1)
      setValue(cityPath, cities[0].city)
  }, [cities.length])

  useEffect(() => {
    const parsed = parse(postCodeField?.toString() ?? '')
    const formatted = formatPsc(parsed)

    setPscString(formatted ?? '')
  }, [postCodeField])

  const [pscString, setPscString] = useState(formatPsc(postCodeField) ?? '')
  useEffect(() => {
    setValidPsc(parse(pscString).length == 5 && !pcError)
  }, [pscString, pcError])

  const handlePscChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    const parsed = parse(value)
    const intVal = parseInt(parsed ? parsed.substring(0, 5) : '')
    setValue(pcPath, isNaN(intVal) ? undefined : intVal)
    setValue(cityPath, undefined)
  }

  return (
    <>
      {street &&
                <FormRow>
                  <FormControl isInvalid={!!streetError} isRequired>
                    <FormLabel>Ulice</FormLabel>
                    <InputGroup placeholder="Např. Dlouhá" htmlSize={24}
                      {...register(streetPath)}
                      isDisabled={isDisabled && isDisabledStreet}
                      errors={streetError}
                    />
                  </FormControl>

                  <FormControl isInvalid={!!streetNumberError} isRequired>
                    <FormLabel>Číslo popisné nebo orientační</FormLabel>

                    <InputGroup placeholder="Např. 123" htmlSize={4} {...register(streetNumberPath)} isDisabled={isDisabled && isDisabledStreet} errors={streetNumberError} />
                  </FormControl>
                </FormRow>
      }

      <FormRow>
        <FormControl isInvalid={!!pcError} isRequired>
          <FormLabel htmlFor={pcPath}>
            PSČ
          </FormLabel>

          <InputGroup
            id={pcPath}
            placeholder="### ##"
            autoComplete="postal-code"
            errors={pcError as FieldError}
            htmlSize={4}
            isDisabled={isDisabled}
            // {...register(pcPath, {
            //     onChange: (e) => e.target.value = formatPsc(e.target.value) ?? ''
            // })}
            value={pscString}
            onChange={handlePscChange}
            onBlur={() => trigger(pcPath)}
          />

          {!isDisabled && <FormHelperText>
            Údaj lze nalézt na webových stránkách{' '}
            <Link href="https://www.psc.cz" target="_blank" rel="noreferrer" variant='underline'>
              www.psc.cz
            </Link>
            .
          </FormHelperText>
          }
        </FormControl>

        <FormControl isInvalid={!!cityError} isRequired>
          <FormLabel>Město</FormLabel>

          {(cities?.length ?? 0) > 0 &&
                        <Select isDisabled={!validPsc || cities.length === 0 || isDisabled} {...register(banameof(x => x.city))}>
                          <option disabled={false} value=''> {/* ponechat volbu vyberte obec kvůli Poleasingu JAPu - aby se automaticky nevybírala první obec ze seznamu */}
                            Vyberte obec
                          </option>
                          {
                            cities.map((item, i) =>
                              <option value={item.city} key={i}>{item.city}</option>
                            )
                          }
                        </Select>
          }

          {(cities?.length ?? 0) == 0 &&
                        <Select isDisabled={true} defaultValue='' icon={isLoadingCities ? <Spinner /> : undefined}>
                          <option disabled value=''>Vyberte obec</option>
                        </Select>
          }

          {cityError && <FormErrorMessage>{cityError.message}</FormErrorMessage>}
        </FormControl>
      </FormRow>
    </>
  )
}

export default InsureeAddress